import { z } from "zod";
import type { ZodType } from "zod";

import type { ActionConfig, ActionEntry } from "../action/types";
import type { ServerContext } from "../types/server-context";

/** Data params of a module that declares none. */
export type NoDataParams = Record<never, never>;

/**
 * Schema a module declares for its data params — the values its dataFetcher
 * and actions receive as `ctx.dataParams`, supplied per module ref by the
 * view that mounts it.
 */
export type ModuleDataParamsSchema = z.ZodType<
  Record<string, unknown>,
  z.ZodTypeDef,
  Record<string, unknown>
>;

/** Default data params schema for modules that declare none. */
export const emptyDataParams = z.object({});
export type EmptyDataParamsSchema = typeof emptyDataParams;

/**
 * Server context for a module's dataFetcher and actions. Modules receive
 * their declared `dataParams` instead of the view's raw `pathParams`, so a
 * module never depends on the route param names of the views that mount it.
 */
export type ModuleServerContext<TDataParams = NoDataParams> = Omit<
  ServerContext,
  "pathParams"
> & {
  dataParams: TDataParams;
};

export type ModuleDataFetcherContext<TDataParams = NoDataParams> =
  ModuleServerContext<TDataParams>;

/**
 * Data params a view must supply for a module (the schema's input type), or
 * {@link NoDataParams} for a definition without `dataParams` — including
 * version-types snapshots taken before data params existed.
 */
export type ModuleDataParams<TModule> = TModule extends {
  dataParams: infer TSchema extends z.ZodTypeAny;
}
  ? z.input<TSchema>
  : NoDataParams;

/**
 * Bind action creation to a module's data params schema so handlers receive
 * a typed `ctx.dataParams`.
 *
 * @example
 * ```ts
 * export const dataParams = z.object({ applicationId: z.string() });
 * export const moduleAction = createModuleAction(dataParams);
 *
 * export const save = moduleAction(saveSchema, async (data, ctx) => {
 *   await persist(ctx.dataParams.applicationId, data);
 * });
 * ```
 */
export function createModuleAction<
  TDataParamsSchema extends ModuleDataParamsSchema,
>(_dataParams: TDataParamsSchema) {
  return function moduleAction<TSchema extends ZodType, TOutput = void>(
    schema: TSchema,
    handler: (
      data: z.output<TSchema>,
      ctx: ModuleServerContext<z.output<TDataParamsSchema>>,
    ) => Promise<TOutput>,
    config?: ActionConfig,
  ): ActionEntry<
    TSchema,
    TOutput,
    ModuleServerContext<z.output<TDataParamsSchema>>
  > {
    return { schema, handler, config };
  };
}

/** Action creator for modules that declare no data params. */
export const moduleAction = createModuleAction(emptyDataParams);
